import React, { useRef, useState } from 'react';
import { Row, Col, Form, Button, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
    MdFlag,
    MdFlight,
    MdPeople,
    MdAttachFile,
    MdPerson,
    MdPersonAdd,
    MdClose,
    MdExpandMore,
    MdExpandLess,
    MdCheck,
    MdAdd,
} from 'react-icons/md';
import { createIncident, selectCreateStatus } from '_redux/slices/incidents/incidents';
import { selectFirebaseInfo } from '_redux/slices/main-screen/user';
import { Colors } from '_styles';
import EmployeePicker from './EmployeePicker';
import { INCIDENT_TYPES } from '_utils/constants';

//#region styles — mirrors booking-form.js section-card pattern
const sectionCard = {
    background: '#fff',
    border: `1px solid ${Colors.GRAY_LIGHT}`,
    borderRadius: 12,
    padding: '24px 28px',
    marginBottom: 20,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
};

const sectionHeader = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 20,
};

const sectionHeaderClickable = {
    ...sectionHeader,
    cursor: 'pointer',
    userSelect: 'none',
};

const sectionCompleteButton = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 16,
    padding: '8px 20px',
    border: 'none',
    borderRadius: 8,
    background: Colors.PRM_LIGHT_BLUE,
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(48, 127, 226, 0.25)',
};

const completedChip = {
    marginLeft: 8,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 10px',
    borderRadius: 999,
    background: '#D1E7DD',
    color: '#155724',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
};

const sectionTitle = {
    fontSize: 16,
    fontWeight: 600,
    color: Colors.TEXT_COLOR,
    margin: 0,
};

const stepBadge = {
    width: 28,
    height: 28,
    borderRadius: '50%',
    backgroundColor: Colors.PRM_LIGHT_BLUE,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 600,
    flexShrink: 0,
};

const submitButton = {
    padding: '12px 40px',
    fontSize: 15,
    fontWeight: 600,
    borderRadius: 8,
    border: 'none',
    backgroundColor: Colors.PRM_LIGHT_BLUE,
    color: '#fff',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    boxShadow: '0 2px 8px rgba(48, 127, 226, 0.3)',
};

const errorBanner = {
    marginTop: 12,
    padding: '10px 14px',
    background: Colors.LIGHT_RED,
    borderRadius: 8,
    borderLeft: `4px solid ${Colors.DARK_RED}`,
};

const errorText = {
    color: Colors.DARK_RED,
    margin: 0,
    fontSize: 14,
    fontWeight: 500,
};

const LABEL_STYLE = {
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 4,
};

const REQUIRED_STAR = { color: Colors.DARK_RED, marginLeft: 2 };
//#endregion

// Canonical incident classifications — stored verbatim on the report and shown
// in the incident list. Keep in sync with the employee app (incidentForm.tsx).

const IncidentForm = ({ onSuccess, onCancel }) => {
    const dispatch = useDispatch();
    const dbUser = useSelector(selectFirebaseInfo);
    const createStatus = useSelector(selectCreateStatus);

    const [title, setTitle] = useState('');
    const [incidentType, setIncidentType] = useState('');
    const [description, setDescription] = useState('');
    const [issue, setIssue] = useState('');
    const [actionTaken, setActionTaken] = useState('');
    const [recommendation, setRecommendation] = useState('');
    const [location, setLocation] = useState('');
    const [date, setDate] = useState('');
    const [flightNumber, setFlightNumber] = useState('');
    const [passengers, setPassengers] = useState([{ id: 1, firstName: '', lastName: '', seatNumber: '', assistanceRequirements: '' }]);
    const [assignedEmployees, setAssignedEmployees] = useState([]);
    const [files, setFiles] = useState([]);
    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState(null);
    const [activeSection, setActiveSection] = useState(1);
    const [completedSections, setCompletedSections] = useState({});
    const fileInputRef = useRef(null);

    const TOTAL_SECTIONS = 5;

    const toggleSection = (id) => {
        setActiveSection((curr) => (curr === id ? null : id));
    };

    const completeSection = (id) => {
        setCompletedSections((prev) => ({ ...prev, [id]: true }));
        setActiveSection(id < TOTAL_SECTIONS ? id + 1 : null);
    };

    const MAX_FILES = 3;

    const addFiles = (newList) => {
        const incoming = Array.from(newList || []);
        if (incoming.length === 0) return;
        setFiles((prev) => {
            const slots = MAX_FILES - prev.length;
            if (slots <= 0) return prev;
            const existingKeys = new Set(prev.map((f) => `${f.name}-${f.size}-${f.lastModified}`));
            const deduped = incoming
                .filter((f) => !existingKeys.has(`${f.name}-${f.size}-${f.lastModified}`))
                .slice(0, slots);
            return [...prev, ...deduped];
        });
    };

    const removeFile = (idx) => {
        setFiles((prev) => prev.filter((_, i) => i !== idx));
    };

    const addPassenger = () => {
        setPassengers(prev => [...prev, { id: Date.now(), firstName: '', lastName: '', seatNumber: '', assistanceRequirements: '' }]);
    };

    const removePassenger = (id) => {
        setPassengers(prev => prev.filter(p => p.id !== id));
    };

    const updatePassenger = (id, field, value) => {
        setPassengers(prev => prev.map(p => p.id === id ? { ...p, [field]: value } : p));
    };

    const validate = () => {
        const e = {};
        if (!title.trim()) e.title = 'Title is required.';
        if (!incidentType) e.incidentType = 'Incident type is required.';
        if (!description.trim()) e.description = 'Overview is required.';
        if (!date) e.date = 'Incident date is required.';
        if (!flightNumber.trim()) e.flightNumber = 'Flight number is required.';
        if (assignedEmployees.length === 0) e.employees = 'At least one employee must be assigned.';
        return e;
    };

    const handleSubmit = async (evt) => {
        evt.preventDefault();
        setSubmitError(null);

        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        setErrors({});

        const iata = dbUser?.details?.authority;
        const adminUserId = dbUser?.firebaseId ?? dbUser?.uid ?? null;

        const acknowledgements = {};
        assignedEmployees.forEach((empId) => {
            acknowledgements[empId] = { status: 'pending', notes: null, acknowledgedAt: null };
        });

        const encodedFiles = await Promise.all(
            files.map((file) => new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve({
                    filename: file.name,
                    contentType: file.type,
                    data: reader.result.split(',')[1],
                });
                reader.onerror = reject;
                reader.readAsDataURL(file);
            }))
        );

        const incidentData = {
            title: title.trim(),
            incidentType,
            description: description.trim(),
            issue: issue.trim() || null,
            actionTaken: actionTaken.trim() || null,
            recommendation: recommendation.trim() || null,
            location: location.trim() || null,
            date: new Date(date).toISOString(),
            flightNumber: flightNumber.trim(),
            passengers: passengers
                .filter(p => p.firstName.trim() || p.lastName.trim())
                .map(({ firstName, lastName, seatNumber, assistanceRequirements }) => ({
                    firstName: firstName.trim(),
                    lastName: lastName.trim(),
                    seatNumber: seatNumber.trim() || null,
                    assistanceRequirements: assistanceRequirements.trim() || null,
                })),
            assignedEmployees,
            acknowledgements,
            createdBy: adminUserId,
            status: 'open',
            iata,
            files: encodedFiles,
        };

        const result = await dispatch(createIncident(incidentData));

        if (result.meta?.requestStatus === 'rejected') {
            setSubmitError(result.error?.message ?? 'Failed to create incident. Please try again.');
            return;
        }

        onSuccess?.();
    };

    const isSubmitting = createStatus === 'loading';

    return (
        <Form onSubmit={handleSubmit} style={{ width: '100%', marginTop: 8 }} noValidate>

            {/* ── Section 1: Incident Details ── */}
            <div style={sectionCard}>
                <div
                    style={{
                        ...sectionHeaderClickable,
                        marginBottom: activeSection === 1 ? 20 : 0,
                    }}
                    onClick={() => toggleSection(1)}
                >
                    <div style={stepBadge}>1</div>
                    <MdFlag size={18} color={Colors.PRM_LIGHT_BLUE} />
                    <h3 style={sectionTitle}>Incident Details</h3>
                    {completedSections[1] && (
                        <span style={completedChip}><MdCheck size={12} /> Complete</span>
                    )}
                    <span style={{ marginLeft: 'auto', color: '#6b7280', display: 'flex' }}>
                        {activeSection === 1 ? <MdExpandLess size={22} /> : <MdExpandMore size={22} />}
                    </span>
                </div>

                {activeSection === 1 && (<>
                    <Row>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label style={LABEL_STYLE}>
                                    Title <span style={REQUIRED_STAR}>*</span>
                                </Form.Label>
                                <Form.Control
                                    placeholder="e.g. Passenger fall at Gate 12"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    isInvalid={!!errors.title}
                                    style={{ borderRadius: 8, fontSize: 14 }}
                                />
                                <Form.Control.Feedback type="invalid">{errors.title}</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label style={LABEL_STYLE}>
                                    Incident Date <span style={REQUIRED_STAR}>*</span>
                                </Form.Label>
                                <Form.Control
                                    type="date"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                    isInvalid={!!errors.date}
                                    style={{ borderRadius: 8, fontSize: 14 }}
                                />
                                <Form.Control.Feedback type="invalid">{errors.date}</Form.Control.Feedback>
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label style={LABEL_STYLE}>
                                    Incident Type <span style={REQUIRED_STAR}>*</span>
                                </Form.Label>
                                <Form.Select
                                    value={incidentType}
                                    onChange={(e) => setIncidentType(e.target.value)}
                                    isInvalid={!!errors.incidentType}
                                    style={{ borderRadius: 8, fontSize: 14 }}
                                >
                                    <option value="">Select incident type…</option>
                                    {INCIDENT_TYPES.map((type) => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">{errors.incidentType}</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>



                    <Row>
                        <Col>
                            <Form.Group className="mb-3">
                                <Form.Label style={LABEL_STYLE}>
                                    Overview <span style={REQUIRED_STAR}>*</span>
                                </Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    placeholder="Describe what happened, when, and any immediate actions taken…"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    isInvalid={!!errors.description}
                                    style={{ borderRadius: 8, fontSize: 14, resize: 'vertical' }}
                                />
                                <Form.Control.Feedback type="invalid">{errors.description}</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <Form.Group className="mb-3">
                                <Form.Label style={LABEL_STYLE}>Issue</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    placeholder="Describe the specific issue…"
                                    value={issue}
                                    onChange={(e) => setIssue(e.target.value)}
                                    style={{ borderRadius: 8, fontSize: 14, resize: 'vertical' }}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <Form.Group className="mb-3">
                                <Form.Label style={LABEL_STYLE}>Action Taken</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    placeholder="What actions were taken at the time…"
                                    value={actionTaken}
                                    onChange={(e) => setActionTaken(e.target.value)}
                                    style={{ borderRadius: 8, fontSize: 14, resize: 'vertical' }}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <Form.Group className="mb-0">
                                <Form.Label style={LABEL_STYLE}>Recommendation</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    placeholder="Any recommendations to prevent recurrence…"
                                    value={recommendation}
                                    onChange={(e) => setRecommendation(e.target.value)}
                                    style={{ borderRadius: 8, fontSize: 14, resize: 'vertical' }}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <button type="button" style={sectionCompleteButton} onClick={() => completeSection(1)}>
                        <MdCheck size={16} /> Complete
                    </button>
                </>)}
            </div>

            {/* ── Section 2: Flight & Location ── */}
            <div style={sectionCard}>
                <div
                    style={{
                        ...sectionHeaderClickable,
                        marginBottom: activeSection === 2 ? 20 : 0,
                    }}
                    onClick={() => toggleSection(2)}
                >
                    <div style={stepBadge}>2</div>
                    <MdFlight size={18} color={Colors.PRM_LIGHT_BLUE} />
                    <h3 style={sectionTitle}>Flight &amp; Location</h3>
                    {completedSections[2] && (
                        <span style={completedChip}><MdCheck size={12} /> Complete</span>
                    )}
                    <span style={{ marginLeft: 'auto', color: '#6b7280', display: 'flex' }}>
                        {activeSection === 2 ? <MdExpandLess size={22} /> : <MdExpandMore size={22} />}
                    </span>
                </div>

                {activeSection === 2 && (<>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label style={LABEL_STYLE}>
                                    Flight Number <span style={REQUIRED_STAR}>*</span>
                                </Form.Label>
                                <Form.Control
                                    placeholder="e.g. EI401"
                                    value={flightNumber}
                                    // Flight numbers are alphanumeric, max 6 chars (e.g. EI401, BA2490).
                                    // Strip any special characters and uppercase as the user types.
                                    onChange={(e) => setFlightNumber(e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 6))}
                                    maxLength={6}
                                    isInvalid={!!errors.flightNumber}
                                    style={{ borderRadius: 8, fontSize: 14 }}
                                />
                                <Form.Control.Feedback type="invalid">{errors.flightNumber}</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label style={LABEL_STYLE}>Location</Form.Label>
                                <Form.Control
                                    placeholder="e.g. Gate 12, Terminal 2"
                                    value={location}
                                    onChange={(e) => setLocation(e.target.value)}
                                    style={{ borderRadius: 8, fontSize: 14 }}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <button type="button" style={sectionCompleteButton} onClick={() => completeSection(2)}>
                        <MdCheck size={16} /> Complete
                    </button>
                </>)}
            </div>

            {/* ── Section 3: Passenger Information ── */}
            <div style={sectionCard}>
                <div
                    style={{
                        ...sectionHeaderClickable,
                        marginBottom: activeSection === 3 ? 16 : 0,
                    }}
                    onClick={() => toggleSection(3)}
                >
                    <div style={stepBadge}>3</div>
                    <MdPerson size={18} color={Colors.PRM_LIGHT_BLUE} />
                    <h3 style={sectionTitle}>Passenger Information</h3>
                    <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 400, marginLeft: 4 }}>Optional</span>
                    {completedSections[3] && (
                        <span style={completedChip}><MdCheck size={12} /> Complete</span>
                    )}
                    {activeSection === 3 && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); addPassenger(); }}
                            style={{
                                marginLeft: 'auto',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                padding: '5px 12px',
                                border: `1px solid ${Colors.PRM_LIGHT_BLUE}`,
                                borderRadius: 6,
                                background: '#fff',
                                color: Colors.PRM_LIGHT_BLUE,
                                fontSize: 13,
                                fontWeight: 600,
                                cursor: 'pointer',
                            }}
                        >
                            <MdPersonAdd size={15} />
                            Add Passenger
                        </button>
                    )}
                    <span style={{ marginLeft: activeSection === 3 ? 8 : 'auto', color: '#6b7280', display: 'flex' }}>
                        {activeSection === 3 ? <MdExpandLess size={22} /> : <MdExpandMore size={22} />}
                    </span>
                </div>

                {activeSection === 3 && (<>

                    {passengers.map((passenger, index) => (
                        <div
                            key={passenger.id}
                            style={{
                                border: '1px solid #e5e7eb',
                                borderRadius: 8,
                                padding: '16px 18px',
                                marginBottom: 12,
                                background: '#fafafa',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                                <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
                                    Passenger {index + 1}
                                </span>
                                {passengers.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removePassenger(passenger.id)}
                                        style={{
                                            marginLeft: 'auto',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 4,
                                            padding: '3px 8px',
                                            border: '1px solid #fca5a5',
                                            borderRadius: 6,
                                            background: '#fff',
                                            color: Colors.DARK_RED,
                                            fontSize: 12,
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <MdClose size={13} />
                                        Remove
                                    </button>
                                )}
                            </div>

                            <Row>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label style={LABEL_STYLE}>First Name</Form.Label>
                                        <Form.Control
                                            placeholder="e.g. Jane"
                                            value={passenger.firstName}
                                            onChange={e => updatePassenger(passenger.id, 'firstName', e.target.value)}
                                            style={{ borderRadius: 8, fontSize: 14 }}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label style={LABEL_STYLE}>Last Name</Form.Label>
                                        <Form.Control
                                            placeholder="e.g. Smith"
                                            value={passenger.lastName}
                                            onChange={e => updatePassenger(passenger.id, 'lastName', e.target.value)}
                                            style={{ borderRadius: 8, fontSize: 14 }}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Form.Group className="mb-0">
                                        <Form.Label style={LABEL_STYLE}>Seat Number</Form.Label>
                                        <Form.Control
                                            placeholder="e.g. 14A"
                                            value={passenger.seatNumber}
                                            onChange={e => updatePassenger(passenger.id, 'seatNumber', e.target.value)}
                                            style={{ borderRadius: 8, fontSize: 14 }}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-0">
                                        <Form.Label style={LABEL_STYLE}>Assistance Requirements</Form.Label>
                                        <Form.Control
                                            placeholder="e.g. Wheelchair, visual impairment"
                                            value={passenger.assistanceRequirements}
                                            onChange={e => updatePassenger(passenger.id, 'assistanceRequirements', e.target.value)}
                                            style={{ borderRadius: 8, fontSize: 14 }}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </div>
                    ))}

                    <button type="button" style={sectionCompleteButton} onClick={() => completeSection(3)}>
                        <MdCheck size={16} /> Complete
                    </button>
                </>)}
            </div>

            {/* ── Section 4: Assign Employees ── */}
            <div style={{
                ...sectionCard,
                ...(errors.employees ? { border: `1px solid ${Colors.DARK_RED}` } : {}),
            }}>
                <div
                    style={{
                        ...sectionHeaderClickable,
                        marginBottom: activeSection === 4 ? 20 : 0,
                    }}
                    onClick={() => toggleSection(4)}
                >
                    <div style={stepBadge}>4</div>
                    <MdPeople size={18} color={Colors.PRM_LIGHT_BLUE} />
                    <h3 style={sectionTitle}>
                        Assign Employees <span style={REQUIRED_STAR}>*</span>
                    </h3>
                    {completedSections[4] && (
                        <span style={completedChip}><MdCheck size={12} /> Complete</span>
                    )}
                    <span style={{ marginLeft: 'auto', color: '#6b7280', display: 'flex' }}>
                        {activeSection === 4 ? <MdExpandLess size={22} /> : <MdExpandMore size={22} />}
                    </span>
                </div>

                {activeSection === 4 && (<>
                    <EmployeePicker selected={assignedEmployees} onChange={setAssignedEmployees} />

                    {errors.employees && (
                        <p style={{ color: Colors.DARK_RED, fontSize: 13, marginTop: 8, marginBottom: 0 }}>
                            {errors.employees}
                        </p>
                    )}

                    <button type="button" style={sectionCompleteButton} onClick={() => completeSection(4)}>
                        <MdCheck size={16} /> Complete
                    </button>
                </>)}
            </div>

            {/* ── Section 5: Attachments (optional) ── */}
            <div style={{
                ...sectionCard,
                background: Colors.GRAY_LIGHT,
                border: `1px dashed #d1d5db`,
            }}>
                <div
                    style={{
                        ...sectionHeaderClickable,
                        marginBottom: activeSection === 5 ? 20 : 0,
                    }}
                    onClick={() => toggleSection(5)}
                >
                    <div style={{ ...stepBadge, backgroundColor: '#6b7280' }}>5</div>
                    <MdAttachFile size={18} color="#6b7280" />
                    <h3 style={sectionTitle}>Attachments</h3>
                    <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 400, marginLeft: 4 }}>Optional</span>
                    {completedSections[5] && (
                        <span style={completedChip}><MdCheck size={12} /> Complete</span>
                    )}
                    <span style={{ marginLeft: 'auto', color: '#6b7280', display: 'flex' }}>
                        {activeSection === 5 ? <MdExpandLess size={22} /> : <MdExpandMore size={22} />}
                    </span>
                </div>

                {activeSection === 5 && (<>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        style={{ display: 'none' }}
                        onChange={(e) => {
                            addFiles(e.target.files);
                            if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                    />
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={files.length >= MAX_FILES}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '8px 16px',
                            border: `1px solid ${files.length >= MAX_FILES ? '#d1d5db' : Colors.PRM_LIGHT_BLUE}`,
                            borderRadius: 8,
                            background: '#fff',
                            color: files.length >= MAX_FILES ? '#9ca3af' : Colors.PRM_LIGHT_BLUE,
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: files.length >= MAX_FILES ? 'not-allowed' : 'pointer',
                        }}
                    >
                        <MdAdd size={16} />
                        {files.length >= MAX_FILES ? `${MAX_FILES}/${MAX_FILES} files` : files.length > 0 ? 'Add More Files' : 'Add Files'}
                    </button>

                    {files.length > 0 && (
                        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {files.map((f, idx) => (
                                <div
                                    key={`${f.name}-${f.size}-${f.lastModified}-${idx}`}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        padding: '6px 12px',
                                        background: '#fff',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: 6,
                                        fontSize: 13,
                                        color: '#374151',
                                    }}
                                >
                                    <MdAttachFile size={14} color="#6b7280" />
                                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {f.name}
                                    </span>
                                    <span style={{ color: '#9ca3af', fontSize: 12 }}>
                                        {(f.size / 1024).toFixed(1)} KB
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => removeFile(idx)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: 4,
                                            border: 'none',
                                            background: 'transparent',
                                            color: Colors.DARK_RED,
                                            cursor: 'pointer',
                                        }}
                                        aria-label={`Remove ${f.name}`}
                                    >
                                        <MdClose size={16} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    <button type="button" style={sectionCompleteButton} onClick={() => completeSection(5)}>
                        <MdCheck size={16} /> Complete
                    </button>
                </>)}
            </div>

            {/* ── Submit ── */}
            <div style={{ marginTop: 8, paddingTop: 4, paddingBottom: 40 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button
                        type="submit"
                        style={{
                            ...submitButton,
                            opacity: isSubmitting ? 0.7 : 1,
                            cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        }}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Spinner animation="border" size="sm" />
                                {'Submitting…'}
                            </span>
                        ) : 'Submit Incident Report'}
                    </button>
                    {onCancel && (
                        <Button
                            variant="outline-secondary"
                            onClick={onCancel}
                            style={{ borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 500 }}
                        >
                            Cancel
                        </Button>
                    )}
                </div>

                {submitError && (
                    <div style={errorBanner}>
                        <p style={errorText}>{submitError}</p>
                    </div>
                )}
            </div>
        </Form>
    );
};

export default IncidentForm;
